import React, { useMemo } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Line, Legend } from 'recharts';
import { format, parseISO, startOfDay } from 'date-fns';

/**
 * Learning Trends Component
 * Shows historical progress, milestones, streaks, and level progression
 */
export function LearningTrends({ trendsData }) {
  const { profile, badges, xpHistory } = trendsData || {};

  const trendChartData = useMemo(() => {
    if (!xpHistory || xpHistory?.length === 0) return [];

    // Aggregate XP by day and calculate cumulative
    const dailyMap = {};
    let cumulative = 0;

    xpHistory?.forEach(event => {
      const date = format(startOfDay(parseISO(event?.createdAt)), 'MMM dd');
      if (!dailyMap?.[date]) {
        dailyMap[date] = {
          date,
          dailyXp: 0,
          cumulativeXp: 0
        };
      }
      dailyMap[date].dailyXp += event?.amount;
    });

    // Sort by date and calculate cumulative
    return Object.values(dailyMap)?.sort((a, b) => new Date(a.date) - new Date(b.date))?.map(day => {
        cumulative += day?.dailyXp;
        return {
          ...day,
          cumulativeXp: cumulative
        };
      });
  }, [xpHistory]);

  const earnedBadges = useMemo(() => {
    if (!badges) return [];
    return badges?.filter(badge => badge?.status === 'earned')?.sort((a, b) => new Date(b.earnedAt) - new Date(a.earnedAt))?.slice(0, 5);
  }, [badges]);

  if (!profile) {
    return (
      <div className="text-center py-12 text-gray-400">
        Loading learning trends...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="text-center bg-gradient-to-br from-yellow-600/20 to-orange-600/20 rounded-xl p-4 border border-yellow-400/30">
          <div className="text-3xl font-bold text-yellow-400">{profile?.currentLevel}</div>
          <div className="text-xs text-gray-300 mt-1">Current Level</div>
        </div>
        <div className="text-center bg-gradient-to-br from-blue-600/20 to-cyan-600/20 rounded-xl p-4 border border-blue-400/30">
          <div className="text-3xl font-bold text-blue-400">{profile?.dailyStreak}</div>
          <div className="text-xs text-gray-300 mt-1">Day Streak 🔥</div>
        </div>
        <div className="text-center bg-gradient-to-br from-purple-600/20 to-pink-600/20 rounded-xl p-4 border border-purple-400/30">
          <div className="text-3xl font-bold text-purple-400">{profile?.longestStreak}</div>
          <div className="text-xs text-gray-300 mt-1">Longest Streak</div>
        </div>
        <div className="text-center bg-gradient-to-br from-green-600/20 to-emerald-600/20 rounded-xl p-4 border border-green-400/30">
          <div className="text-3xl font-bold text-green-400">
            {profile?.totalLifetimeXp?.toLocaleString()}
          </div>
          <div className="text-xs text-gray-300 mt-1">Total XP</div>
        </div>
      </div>
      {/* XP Trend Over Time */}
      {trendChartData?.length > 0 && (
        <div>
          <h4 className="text-lg font-semibold mb-4 text-white">XP Growth Over Time</h4>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={trendChartData}>
              <defs>
                <linearGradient id="colorXp" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#FFD700" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#FFD700" stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis 
                dataKey="date" 
                stroke="rgba(255,255,255,0.6)"
                style={{ fontSize: '11px' }}
              />
              <YAxis 
                stroke="rgba(255,255,255,0.6)"
                style={{ fontSize: '12px' }}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'rgba(50, 33, 93, 0.95)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '8px',
                  color: '#fff'
                }}
              />
              <Area 
                type="monotone" 
                dataKey="cumulativeXp" 
                stroke="#FFD700" 
                fillOpacity={1} 
                fill="url(#colorXp)"
                name="Cumulative XP"
              />
              <Line 
                type="monotone" 
                dataKey="dailyXp" 
                stroke="#FF6B9D" 
                strokeWidth={2}
                name="Daily XP"
                dot={{ fill: '#FF6B9D', r: 3 }}
              />
              <Legend />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
      {/* Recent Badges */}
      {earnedBadges?.length > 0 && (
        <div>
          <h4 className="text-lg font-semibold mb-3 text-white">Recent Achievements 🏆</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {earnedBadges?.map((badge, index) => (
              <div 
                key={index}
                className="flex items-center gap-3 bg-gradient-to-r from-purple-900/30 to-pink-900/30 rounded-lg p-3 border border-purple-400/20"
              >
                <div className="text-2xl">🏅</div>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-white">{badge?.title}</div>
                  <div className="text-xs text-gray-400">
                    {badge?.category} • {badge?.points} pts
                  </div>
                  {badge?.earnedAt && (
                    <div className="text-xs text-gray-500 mt-1">
                      {format(parseISO(badge?.earnedAt), 'MMM dd, yyyy')}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Predictive Insights */}
      <div className="bg-gradient-to-br from-indigo-600/10 to-purple-600/10 rounded-xl p-4 border border-indigo-400/20">
        <h4 className="text-sm font-semibold mb-2 text-indigo-300">📊 Learning Insights</h4>
        <ul className="space-y-2 text-sm text-gray-300">
          {profile?.dailyStreak >= 7 && (
            <li className="flex items-center gap-2">
              <span className="text-green-400">✓</span>
              Great consistency! Keep up your {profile?.dailyStreak}-day streak.
            </li>
          )}
          {profile?.dailyStreak < 3 && profile?.longestStreak >= 7 && (
            <li className="flex items-center gap-2">
              <span className="text-yellow-400">!</span>
              You've had a {profile?.longestStreak}-day streak before. Let's rebuild it!
            </li>
          )}
          {earnedBadges?.length >= 3 && (
            <li className="flex items-center gap-2">
              <span className="text-purple-400">★</span>
              You've earned {earnedBadges?.length} badges recently. You're on fire!
            </li>
          )}
          <li className="flex items-center gap-2">
            <span className="text-blue-400">→</span>
            At your current pace, you'll reach level {profile?.currentLevel + 1} soon!
          </li>
        </ul>
      </div>
    </div>
  );
}